import { DateFormatter, Translate } from '@components'

import { Color, InternalAccessor, TableColumn, Text } from '@billy/design-system'
import { TFunction } from 'i18next'
import React from 'react'

import { TransactionsTableAccessor } from '../../../enums/TransactionsTableAccessor'
import { AttachmentPreviewTarget } from '../../../types/attachmentPreviewTarget'
import { TransactionsTableRow } from '../../../types/transactionsTableRow'
import { getTransactionsTableColumnCell } from '../../../utils/getTransactionsTableColumnCell'
import { TransactionsTableAttachmentCell } from '../../TransactionsTableAttachmentCell'
import { TransactionsTableDescriptionCell } from '../../TransactionsTableDescriptionCell'
import { TransactionsTableHoverActions } from '../../TransactionsTableHoverActions'
import { TransactionsTableJournalCodeCell } from '../../TransactionsTableJournalCodeCell'
import { TransactionsTableOriginLink } from '../../TransactionsTableOriginLink'
import { TransactionsTableStaticActions } from '../../TransactionsTableStaticActions'

interface AttachmentPreview {
  loadingTransactionId: string | undefined
  openAttachmentPreview: (target: AttachmentPreviewTarget) => void
}

interface GetTransactionTableColumnsArgs {
  attachmentPreview: AttachmentPreview
  isFrenchOrganization: boolean
  t: TFunction
}

export const getTransactionTableColumns = ({
  attachmentPreview,
  isFrenchOrganization,
  t,
}: GetTransactionTableColumnsArgs): TableColumn<TransactionsTableRow>[] => {
  const tableColumns: TableColumn<TransactionsTableRow>[] = [
    {
      accessor: TransactionsTableAccessor.TransactionNo,
      Cell: getTransactionsTableColumnCell,
      fixedSize: true,
      Header: <Translate value="transactions.table.column.transaction_no" />,
      size: 'xs',
      sortable: true,
      truncate: true,
    },
    {
      accessor: TransactionsTableAccessor.EntryDate,
      Cell: ({ value: date, ...rest }) => {
        return getTransactionsTableColumnCell({ ...rest, value: date && <DateFormatter value={date} /> })
      },
      fixedSize: true,
      Header: <Translate value="transactions.table.column.date" />,
      size: 's',
      sortable: true,
    },
    {
      accessor: TransactionsTableAccessor.VoucherNo,
      Cell: getTransactionsTableColumnCell,
      Header: <Translate value="transactions.table.column.voucher_no" />,
      hidden: true,
      size: 'xxs',
      sortable: true,
      truncate: true,
      alignment: 'center',
    },
    {
      accessor: TransactionsTableAccessor.Attachment,
      Cell: ({ data, row, value: hasAttachment }) => {
        const { id: transactionId, originatorId, originatorType, voided } = data[row.id]
        const { loadingTransactionId, openAttachmentPreview } = attachmentPreview

        return (
          <TransactionsTableAttachmentCell
            hasAttachment={hasAttachment}
            loadingTransactionId={loadingTransactionId}
            openAttachmentPreview={openAttachmentPreview}
            originatorId={originatorId}
            originatorType={originatorType}
            transactionId={transactionId}
            voided={voided}
          />
        )
      },
      fixedSize: true,
      Header: '',
      headerName: t('transactions.table.column.attachment'),
      size: 'xxs',
      skipOnRowClick: true,
      sortable: true,
    },
    {
      accessor: TransactionsTableAccessor.OriginatorTypeKey,
      Cell: getTransactionsTableColumnCell,
      Header: <Translate value="transactions.table.column.type" />,
      hidden: true,
      size: 'xs',
      truncate: true,
    },
    ...(isFrenchOrganization
      ? ([
          {
            accessor: TransactionsTableAccessor.JournalCode,
            Cell: ({ data, row, value: journalCode }) => {
              const { journalName } = data[row.id]

              return <TransactionsTableJournalCodeCell journalCode={journalCode} journalName={journalName} />
            },
            Header: <Translate value="transactions.table.column.journal_code" />,
            size: 'xs',
            truncate: true,
            alignment: 'center',
          },
        ] as TableColumn<TransactionsTableRow>[])
      : []),
    {
      accessor: TransactionsTableAccessor.Origin,
      Cell: ({ data, row, value: originUrl }) => {
        const { voided } = data[row.id]

        return originUrl && <TransactionsTableOriginLink url={originUrl} voided={voided} />
      },
      fixedSize: true,
      Header: <Translate value="transactions.table.column.origin" />,
      hidden: true,
      size: 'xs',
      skipOnRowClick: true,
      alignment: 'center',
    },
    {
      accessor: TransactionsTableAccessor.Description,
      Cell: ({ data, row, value }) => {
        const { voided } = data[row.id]

        return <TransactionsTableDescriptionCell value={value} voided={voided} />
      },
      Header: <Translate value="transactions.table.column.description" />,
      size: 'l',
    },
    {
      accessor: TransactionsTableAccessor.CreatedBy,
      Cell: getTransactionsTableColumnCell,
      Header: <Translate value="transactions.table.column.created_by" />,
      hidden: true,
      size: 'xs',
      truncate: true,
    },
    {
      accessor: TransactionsTableAccessor.CreatedDate,
      Cell: ({ value: date, ...rest }) => {
        return getTransactionsTableColumnCell({ ...rest, value: date && <DateFormatter value={date} /> })
      },
      fixedSize: true,
      Header: <Translate value="transactions.table.column.created" />,
      hidden: true,
      size: 's',
    },
    {
      accessor: TransactionsTableAccessor.Amount,
      alignment: 'right',
      Cell: ({ value, ...rest }) => {
        const { voided } = rest.data[rest.row.id]

        return getTransactionsTableColumnCell({
          ...rest,
          value: voided ? (
            <Text inline strikethrough color={Color.Grey500}>
              {value}
            </Text>
          ) : (
            value
          ),
        })
      },
      Header: <Translate value="transactions.table.column.amount" />,
      size: 'xs',
      truncate: true,
    },
    {
      accessor: TransactionsTableAccessor.Currency,
      Cell: getTransactionsTableColumnCell,
      fixedSize: true,
      Header: '',
      headerName: t('transactions.table.column.currency'),
      size: 'xxs',
    },
    {
      accessor: InternalAccessor.HoverActions,
      Header: '',
      Cell: ({ row, data }) => {
        const { originatorId, originatorType, voided } = data[row.id] as TransactionsTableRow

        return (
          <TransactionsTableHoverActions
            isVoided={voided}
            originatorId={originatorId}
            originatorType={originatorType}
          />
        )
      },
      sortable: false,
    },
    {
      accessor: InternalAccessor.StaticActions,
      Header: '',
      Cell: ({ row, data }) => {
        const { id, originatorId, originatorType, voided } = data[row.id] as TransactionsTableRow

        return (
          <TransactionsTableStaticActions
            isVoided={voided}
            originatorId={originatorId}
            originatorType={originatorType}
            transactionId={id}
          />
        )
      },
      sortable: false,
    },
  ]

  return tableColumns
}
